import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { FeedbackRibbon, FeedbackDialogParams, FeedbackCategory } from 'owa-feedback';
import { feedbackSubmitted } from 'owa-search';
import { onFeedbackSubmitted } from 'owa-search-next-lazy';
import sendSearchFeedback from '../helpers/sendSearchFeedback';
import { SubstrateSearchScenario } from 'owa-search-service';
import {
    SearchInContextFeedbackProps,
    UserVoiceSearchTeam,
} from '../components/SearchInContextFeedback';
import { isFeatureEnabled } from 'owa-feature-flags';
import { SEARCH_DIAGNOSTICS_PANEL_KEY, searchDiagnosticsStore } from 'owa-search-diagnostics';
import loc, { format, formatToArray } from 'owa-localize';
import {
    dialogTitle,
    feedbackCommentsPlaceholder,
    feedbackLauncherLink,
    searchFeedbackThankYouMessage,
    moreFeedback,
    noResultsOption,
    slowSearchOption,
    notRelevantOption,
    missingResultOption,
    uselessSuggestionsOption,
    hardToUseOption,
    otherOption,
    elapsedTimeMessage,
    elapsedTimeValueInMilliseconds,
} from './SearchInlineFeedback.locstring.json';
import { HeaderCharmType, toggleCharm } from 'owa-header-store';
import { lazySetSelectedPivot } from 'owa-diagnostics';
import { Link } from 'office-ui-fabric-react/lib/components/Link';
import { isBrowserIE } from 'owa-user-agent/lib/userAgent';

const styles = require('./SearchInContextFeedbackDialog.scss');
const classNames = require('classnames');

const feedbackStrings = {
    dialogTitle: loc(dialogTitle),
    feedbackCommentsPlaceholder: loc(feedbackCommentsPlaceholder),
    feedbackLauncherLink: loc(feedbackLauncherLink),
    searchFeedbackThankYouMessage: loc(searchFeedbackThankYouMessage),
    moreFeedback: loc(moreFeedback),
    noResultsOption: loc(noResultsOption),
    slowSearchOption: loc(slowSearchOption),
    notRelevantOption: loc(notRelevantOption),
    missingResultOption: loc(missingResultOption),
    uselessSuggestionsOption: loc(uselessSuggestionsOption),
    hardToUseOption: loc(hardToUseOption),
    otherOption: loc(otherOption),
};

const SearchInlineFeedback = observer(function SearchInlineFeedback(
    props: SearchInContextFeedbackProps
) {
    const feedbackCategories: FeedbackCategory[] = [
        { logString: 'No results were found', uiString: feedbackStrings.noResultsOption },
        { logString: 'Search was slow', uiString: feedbackStrings.slowSearchOption },
        {
            logString: "The results weren't relevant",
            uiString: feedbackStrings.notRelevantOption,
        },
        {
            logString: "The item I was looking for wasn't in the results",
            uiString: feedbackStrings.missingResultOption,
        },
        {
            logString: "Search suggestions weren't useful",
            uiString: feedbackStrings.uselessSuggestionsOption,
        },
        { logString: 'Search is hard to use', uiString: feedbackStrings.hardToUseOption },
        { logString: 'Other', uiString: feedbackStrings.otherOption },
    ];

    const feedbackDialogParams: FeedbackDialogParams = {
        title: feedbackStrings.dialogTitle,
        categories: feedbackCategories,
        ghostText: feedbackStrings.feedbackCommentsPlaceholder,
        createUVTicket: true,
        submitCallBack: (
            inlineFeedbackResponse: string,
            inlineFeedbackResponseCategories: string[]
        ) => {
            sendSearchFeedback(
                props.userVoiceTeam,
                inlineFeedbackResponse,
                inlineFeedbackResponseCategories
            );
        },
    };

    const onYesSelected = () => {
        if (isFeatureEnabled('sea-lazy2')) {
            onFeedbackSubmitted(
                props.userVoiceTeam == UserVoiceSearchTeam.MailSearch
                    ? SubstrateSearchScenario.Mail
                    : SubstrateSearchScenario.Calendar,
                true,
                props.currentSearchQueryId,
                feedbackStrings.feedbackLauncherLink
            );
        } else {
            feedbackSubmitted(
                props.userVoiceTeam == UserVoiceSearchTeam.MailSearch
                    ? SubstrateSearchScenario.Mail
                    : SubstrateSearchScenario.Calendar,
                true,
                props.currentSearchQueryId,
                feedbackStrings.feedbackLauncherLink
            );
        }
    };

    const onNoSelected = () => {
        if (isFeatureEnabled('sea-lazy2')) {
            onFeedbackSubmitted(
                props.userVoiceTeam == UserVoiceSearchTeam.MailSearch
                    ? SubstrateSearchScenario.Mail
                    : SubstrateSearchScenario.Calendar,
                false,
                props.currentSearchQueryId,
                feedbackStrings.feedbackLauncherLink
            );
        } else {
            feedbackSubmitted(
                props.userVoiceTeam == UserVoiceSearchTeam.MailSearch
                    ? SubstrateSearchScenario.Mail
                    : SubstrateSearchScenario.Calendar,
                false,
                props.currentSearchQueryId,
                feedbackStrings.feedbackLauncherLink
            );
        }
    };

    const searchTimeInMilliseconds = getSearchTimeInMilliseconds(props.currentSearchQueryId);

    const elapsedTimeElement = format(
        loc(elapsedTimeValueInMilliseconds),
        searchTimeInMilliseconds
    );

    /**
     * Toggling diagnostics panel in this way does not work in IE. Given that
     * this is an internal, developer-focused feature and not many (if any) internal
     * developers actively use IE, let's remove the click target instead of
     * investing in a fix (because we can't even run dev builds in IE).
     */
    const elapsedTimeToRender = isBrowserIE() ? (
        elapsedTimeElement
    ) : (
        <Link onClick={onElapsedTimeClick}>{elapsedTimeElement}</Link>
    );

    return (
        <div className={classNames(props.cssClass, styles.inlineRibbonContainer)}>
            <FeedbackRibbon
                ribbonMessage={feedbackStrings.feedbackLauncherLink}
                thankYouMessage={feedbackStrings.searchFeedbackThankYouMessage}
                moreFeedbackRibbonMessage={feedbackStrings.moreFeedback}
                feature="Search"
                metadata={props.currentSearchQueryId}
                yesCallBack={onYesSelected}
                noCallBack={onNoSelected}
                noFeedbackDialogParams={feedbackDialogParams}
                yesFeedbackDialogParams={feedbackDialogParams}
                ribbonMessageClassName={styles.searchFeedbackRibbonMessage}
            />
            <span className={styles.elapsedTime}>
                {searchTimeInMilliseconds
                    ? formatToArray(loc(elapsedTimeMessage), elapsedTimeToRender)
                    : ''}
            </span>
        </div>
    );
});

const onElapsedTimeClick = () => {
    toggleCharm(HeaderCharmType.DiagnosticsCharm);
    lazySetSelectedPivot
        .import()
        .then(setSelectedPivot => setSelectedPivot(SEARCH_DIAGNOSTICS_PANEL_KEY));
};

const getSearchTimeInMilliseconds = (currentSearchQueryId: string) => {
    const searchInstrumentationEntries = searchDiagnosticsStore.searchInstrumentationList
        .slice()
        .reverse();

    for (const instrumentation of searchInstrumentationEntries) {
        if (
            instrumentation.PageNumber == 0 &&
            instrumentation.SearchQueryId == currentSearchQueryId
        ) {
            return instrumentation.ClientJSEndToEndTime;
        }
    }

    return 0;
};

export default SearchInlineFeedback;
